import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  isSourceStale,
  type SourceDto,
  SourceSchema,
  type TestRunResponse,
  TestRunResponseSchema,
} from '@vision360/contracts';
import { Pause, Pencil, Play, PlayCircle, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useApi } from '@/lib/auth/session-context';
import { cn } from '@/lib/utils';
import { absoluteTime, ago, formatDuration } from '@/widgets/_sdk/format';
import { SOURCES_KEY, useSourceStats, useSources } from '@/widgets/_sdk/hooks';
import { SourceDialog } from './source-dialog';

/**
 * Source health (PRD VS-015): status, last success, last error, items in the
 * last 24 h, a stale flag at 3× the poll interval, pause/resume and test-run.
 * Status changes arrive live through the stream (they refetch `['sources']`).
 */

const STATUS_CLASS: Record<SourceDto['status'], string> = {
  active: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  degraded: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  paused: 'bg-muted text-muted-foreground',
  error: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

function When({ iso, now }: { iso: string | null; now: number }) {
  const { t } = useTranslation('sources');
  if (!iso) return <span className="text-muted-foreground">{t('never')}</span>;
  return (
    <time dateTime={iso} title={absoluteTime.format(new Date(iso))}>
      {ago(iso, now)}
    </time>
  );
}

function SourceRow({
  source,
  stats,
  now,
  onEdit,
}: {
  source: SourceDto;
  stats: { events24h: number; metrics24h: number } | undefined;
  now: number;
  onEdit: (source: SourceDto) => void;
}) {
  const { t } = useTranslation('sources');
  const api = useApi();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<TestRunResponse | null>(null);

  const pause = useMutation({
    mutationFn: (paused: boolean) =>
      api.request(`/api/v1/sources/${source.id}/pause`, {
        method: 'POST',
        body: { paused },
        schema: SourceSchema,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SOURCES_KEY }),
  });
  const test = useMutation({
    mutationFn: () =>
      api.request(`/api/v1/sources/${source.id}/test`, {
        method: 'POST',
        schema: TestRunResponseSchema,
      }),
    onMutate: () => setResult(null),
    onSuccess: setResult,
  });
  const stale = isSourceStale(source, now);

  return (
    <tr className="align-top">
      <th scope="row" className="py-3 pr-4 text-left font-medium">
        <span className="block">{source.name}</span>
        <span className="block text-xs font-normal text-muted-foreground">
          {source.connectorId} ·{' '}
          {source.mode === 'poll' && source.pollIntervalS
            ? t('mode.poll', { interval: formatDuration(source.pollIntervalS) })
            : t(`mode.${source.mode}`)}
        </span>
      </th>
      <td className="py-3 pr-4">
        <span className="flex flex-wrap gap-1">
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 text-xs font-medium',
              STATUS_CLASS[source.status],
            )}
          >
            {t(`status.${source.status}`)}
          </span>
          {stale && (
            <span className="rounded-sm bg-red-500/15 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
              {t('stale')}
            </span>
          )}
        </span>
      </td>
      <td className="py-3 pr-4 text-sm">
        <When iso={source.lastSuccessAt} now={now} />
      </td>
      <td className="max-w-xs py-3 pr-4 text-sm">
        {source.lastError ? (
          <span className="line-clamp-2 text-destructive" title={source.lastError}>
            {source.lastError}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('none')}</span>
        )}
      </td>
      <td className="py-3 pr-4 text-sm tabular-nums">
        {stats ? t('items', { events: stats.events24h, metrics: stats.metrics24h }) : '—'}
      </td>
      <td className="py-3">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            aria-label={t('editLabel', { name: source.name })}
            onClick={() => onEdit(source)}
          >
            <Pencil aria-hidden="true" className="h-4 w-4" />
            {t('edit')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={pause.isPending}
            aria-label={t(source.enabled ? 'pauseLabel' : 'resumeLabel', { name: source.name })}
            onClick={() => pause.mutate(source.enabled)}
          >
            {source.enabled ? (
              <Pause aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Play aria-hidden="true" className="h-4 w-4" />
            )}
            {source.enabled ? t('pause') : t('resume')}
          </Button>
          {source.mode === 'poll' && (
            <Button
              size="sm"
              variant="outline"
              disabled={test.isPending}
              aria-label={t('testLabel', { name: source.name })}
              onClick={() => test.mutate()}
            >
              {test.isPending ? <Spinner /> : <PlayCircle aria-hidden="true" className="h-4 w-4" />}
              {test.isPending ? t('testing') : t('test')}
            </Button>
          )}
        </div>
        <div aria-live="polite" className="mt-1 max-w-xs text-xs">
          {source.mode !== 'poll' && (
            <span className="text-muted-foreground">{t('testWebhook')}</span>
          )}
          {result &&
            (result.ok ? (
              <span className="text-emerald-700 dark:text-emerald-400">
                {t('testOk', {
                  ms: Math.round(result.durationMs),
                  events: result.events,
                  metrics: result.metrics,
                })}
              </span>
            ) : (
              <span className="text-destructive">
                {t('testFailed', { error: result.error ?? '' })}
              </span>
            ))}
          {(pause.isError || test.isError) && (
            <span className="text-destructive">{t('actionFailed')}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

export function SourcesPage({ now = Date.now }: { now?: () => number }) {
  const { t } = useTranslation('sources');
  const sources = useSources();
  const stats = useSourceStats();
  const byId = new Map((stats.data?.stats ?? []).map((s) => [s.sourceId, s]));
  // {} = a new source; null = no dialog.
  const [editing, setEditing] = useState<{ source?: SourceDto } | null>(null);

  let body: React.ReactNode;
  if (sources.isPending) {
    body = (
      <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
        <Spinner />
        {t('loading')}
      </output>
    );
  } else if (sources.isError) {
    body = (
      <Alert variant="destructive" role="alert">
        {t('error')}
      </Alert>
    );
  } else if (sources.data.sources.length === 0) {
    body = <p className="text-muted-foreground">{t('empty')}</p>;
  } else {
    body = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[48rem] border-collapse">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.name')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.status')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.lastSuccess')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.lastError')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.items')}
              </th>
              <th scope="col" className="py-2 font-medium">
                {t('columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sources.data.sources.map((source) => (
              <SourceRow
                key={source.id}
                source={source}
                stats={byId.get(source.id)}
                now={now()}
                onEdit={(s) => setEditing({ source: s })}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-prose space-y-1">
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
        </div>
        <Button className="h-11" onClick={() => setEditing({})}>
          <Plus aria-hidden="true" className="h-4 w-4" />
          {t('new')}
        </Button>
      </div>
      {body}
      {editing && <SourceDialog source={editing.source} onClose={() => setEditing(null)} />}
    </div>
  );
}
